import { Navigate, Outlet } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import type { UserRole } from "@/lib/types";
import FullScreenState from "@/components/app/FullScreenState";

export default function ProtectedRoute({ role }: { role?: UserRole }) {
  const { currentUser, loading, configError } = useApp();

  if (loading) {
    return (
      <FullScreenState
        title="Loading your workspace"
        description="Schedora is syncing your account and business data."
      />
    );
  }

  if (configError) {
    return (
      <FullScreenState
        title="Configuration needed"
        description={configError}
        actionLabel="Go to Login"
        actionHref="/login"
      />
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (!currentUser.isActive && currentUser.role !== "super_admin") {
    return (
      <FullScreenState
        title="Access disabled"
        description="This account has been deactivated. Contact support or your platform administrator."
        actionLabel="Go to Login"
        actionHref="/login"
      />
    );
  }

  if (role && currentUser.role !== role) {
    return (
      <Navigate
        to={
          currentUser.role === "owner"
            ? "/owner/dashboard"
            : currentUser.role === "staff"
              ? "/staff/dashboard"
            : currentUser.role === "super_admin"
              ? "/admin/dashboard"
              : "/customer/dashboard"
        }
        replace
      />
    );
  }

  return <Outlet />;
}
